/**
 * Global app store (Zustand) for state that persists across route changes.
 * Used by List items and Insert Basic Data so switching pages preserves state.
 * Resets on full page refresh (store recreated).
 */
import { create } from 'zustand';
import { emptyFormData as categoryEmptyFormData } from '../components/insertDataComponents/categoryForm/categoryFormUtils';
import { emptyFormData as productEmptyFormData } from '../components/insertDataComponents/productsForm/productFormUtils';
import { emptyFormData as supplierEmptyFormData } from '../components/insertDataComponents/suppliersForm/supplierFormUtils';
import { emptyFormData as shippingEmptyFormData } from '../components/insertDataComponents/shippingsForm/shippingFormUtils';

const SECTIONS = {
  CATEGORIES: 'categories',
  PRODUCTS: 'products',
  SUPPLIERS: 'suppliers',
  SHIPPINGS: 'shippings'
};

const initialSalesPage = () => ({
  productNameFilter: ''
});

const initialUpdateListingPage = () => ({
  listingSearchQuery: '',
  showListingDropdown: false,
  selectedListing: null,
  showEditListingSuppliersModal: false,
  /** Keyed by listing_id: { [products_suppliers_id]: { selected_quantity, priority } } */
  editListingSupplierSelection: {}
});

const initialListItemsPage = () => ({
  productSearchQuery: '',
  showProductDropdown: false,
  selectedProduct: null,
  mlbDetails: null,
  mlbError: '',
  mlbLoading: false,
  publishLoading: false,
  publishError: '',
  publishSuccess: '',
  /** User overrides for listing preview fields (title, condition, price, etc.). Used on publish and in table. */
  listingOverrides: {},
  /** Pick listing suppliers modal. Keyed by product_id: { [products_suppliers_id]: selected_quantity }. */
  listingSupplierSelection: {},
  showPickListingSuppliersModal: false
});

const initialInsertBasicData = () => ({
  expandedSections: {
    [SECTIONS.CATEGORIES]: false,
    [SECTIONS.PRODUCTS]: false,
    [SECTIONS.SUPPLIERS]: false,
    [SECTIONS.SHIPPINGS]: false
  },
  /** Each section reports its loading state; when any is true, lock add/submit/load across Insert Basic Data. */
  sectionLoading: {
    [SECTIONS.CATEGORIES]: false,
    [SECTIONS.PRODUCTS]: false,
    [SECTIONS.SUPPLIERS]: false,
    [SECTIONS.SHIPPINGS]: false
  }
});

const initialCategoryForm = () => ({
  mode: 'create',
  formData: categoryEmptyFormData(),
  formError: '',
  formSuccess: '',
  submitting: false,
  deleting: false,
  selectedCategoryId: null,
  selectedCategory: null,
  showCategoryDropdown: false,
  showSubCategoryDropdown: false,
  categoryFieldTouched: false,
  showMlbCategoryDropdown: false,
  showMlbChildDropdown: false
});

/**
 * Canonical empty product-supplier row shape. Used when adding a new link and to normalize
 * rows so shared_stock and active (and any future fields) are always present in store state.
 * Keeps state consistent when navigating between List items and Insert Basic Data.
 */
export const getEmptyProductSupplierRow = () => ({
  supplier_id: null,
  origin: '',
  unit_price: null,
  moq: null,
  stored_location: '',
  available_amount: null,
  default_sale_price: null,
  sale_price_currency: '',
  unit_price_currency: '',
  shared_stock: false,
  active: true
});

/** Normalize a product_supplier row from API so it always has shared_stock and active (for store consistency). */
export const normalizeProductSupplierRow = (row) => ({
  ...row,
  shared_stock: row.shared_stock ?? false,
  active: row.active !== false
});

const initialProductsForm = () => ({
  mode: 'create',
  formData: productEmptyFormData(),
  formError: '',
  formSuccess: '',
  submitting: false,
  deleting: false,
  selectedProduct: null,
  productSearchQuery: '',
  showProductDropdown: false,
  productNameFieldTouched: false,
  categoryFieldTouched: false,
  categorySearchQuery: '',
  subCategorySearchQuery: '',
  showCategoryDropdown: false,
  showSubCategoryDropdown: false,
  additionalDetails: [],
  pendingAdditionalDetailDeletes: [],
  productSuppliers: [],
  pendingProductSupplierDeletes: [],
  pendingPictureAdds: [],
  pendingPictureDeletes: [],
  pictureFileError: '',
  openDetailNameDropdownIndex: null,
  openSupplierDropdownIndex: null,
  attributesFromCategoryLoading: false,
  /** Product id we last synced productSuppliers from API for; persists across nav so we don't overwrite unsaved edits. */
  productSuppliersSyncedForProductId: null
});

const initialSuppliersForm = () => ({
  mode: 'create',
  formData: supplierEmptyFormData(),
  formError: '',
  formSuccess: '',
  submitting: false,
  deleting: false,
  selectedSupplier: null,
  supplierSearchQuery: '',
  showSupplierDropdown: false,
  supplierFieldTouched: false,
  contacts: [],
  pendingContactDeletes: []
});

const initialShippingsForm = () => ({
  mode: 'create',
  formData: shippingEmptyFormData(),
  formError: '',
  formSuccess: '',
  submitting: false,
  deleting: false,
  selectedSupplier: null,
  supplierSearchQuery: '',
  showSupplierDropdown: false,
  supplierFieldTouched: false,
  selectedProductSupplier: null,
  productSupplierSearchQuery: '',
  showProductSupplierDropdown: false,
  productSupplierFieldTouched: false,
  selectedShipping: null,
  showShippingDropdown: false,
  shippingSearchQuery: '',
  shippingFieldTouched: false
});

export const useAppStore = create((set) => ({
    salesPage: initialSalesPage(),
    setSalesPage: (arg) =>
      set((state) => ({
        salesPage: typeof arg === 'function' ? arg(state.salesPage) : { ...state.salesPage, ...arg }
      })),

    updateListingPage: initialUpdateListingPage(),
    setUpdateListingPage: (arg) =>
      set((state) => ({
        updateListingPage:
          typeof arg === 'function' ? arg(state.updateListingPage) : { ...state.updateListingPage, ...arg }
      })),

    listItemsPage: initialListItemsPage(),
    setListItemsPage: (arg) =>
      set((state) => ({
        listItemsPage: typeof arg === 'function' ? arg(state.listItemsPage) : { ...state.listItemsPage, ...arg }
      })),

    insertBasicData: initialInsertBasicData(),
    setInsertBasicData: (arg) =>
      set((state) => ({
        insertBasicData:
          typeof arg === 'function' ? arg(state.insertBasicData) : { ...state.insertBasicData, ...arg }
      })),

    categoryForm: initialCategoryForm(),
    setCategoryForm: (arg) =>
      set((state) => ({
        categoryForm: typeof arg === 'function' ? arg(state.categoryForm) : { ...state.categoryForm, ...arg }
      })),

    productsForm: initialProductsForm(),
    setProductsForm: (arg) =>
      set((state) => ({
        productsForm: typeof arg === 'function' ? arg(state.productsForm) : { ...state.productsForm, ...arg }
      })),

    suppliersForm: initialSuppliersForm(),
    setSuppliersForm: (arg) =>
      set((state) => ({
        suppliersForm: typeof arg === 'function' ? arg(state.suppliersForm) : { ...state.suppliersForm, ...arg }
      })),

    shippingsForm: initialShippingsForm(),
    setShippingsForm: (arg) =>
      set((state) => ({
        shippingsForm: typeof arg === 'function' ? arg(state.shippingsForm) : { ...state.shippingsForm, ...arg }
      }))
  }));
